import { useRouter } from 'next/router';
import { useNarrow } from './use-narrow';
import { useActiveConversation } from '../components/chat/ActiveConversationContext';
import { useOnboardingEntry } from './use-onboarding-entry';

/**
 * True when the chat is rendered as a visible surface (a panel/expanded overlay),
 * not a bubble or a plain page.
 *
 * The chat is a companion overlay: it hovers over any page as a panel/expanded
 * surface, with the narrow-home exception (the phone home route already IS the
 * landing, so an empty new chat there stays collapsed).
 *
 * Shared so AppFrame and FloatingChat agree on what's showing — AppFrame uses it
 * to drop the drawer hamburger and dim the page behind an open chat.
 */
export function useChatOverlayOpen(): boolean {
  const router = useRouter();
  const narrow = useNarrow();
  const { chatMode, activeConversationId, keepChatExpanded } = useActiveConversation();
  const { route: onboardingRoute } = useOnboardingEntry();
  const onRoot = router.pathname === '/';
  // Onboarding entry opens the chat so ChatSurface — which hosts the greeting autostart — actually mounts;
  // otherwise the autostart can never run (it lives inside a surface that only mounts once the chat is
  // open). Derived from recipient state (the native `onboardingRoute` port), replacing the old
  // `?welcome=true` param — so this holds on narrow home too, where the empty-new-chat rule below would
  // otherwise keep a kickoff user's chat collapsed and the greeting would never fire.
  //
  // `chatMode !== 'bubble'` so an EXPLICIT minimize releases the force-open: the initial open comes from
  // AppFrame setting `fullscreen`, and once that has happened the user must be able to close the chat on
  // home like they can on every other route. Without this the X (→ setChatMode('bubble')) was overridden
  // straight back to open on `/`, trapping a pack-accept user whose home is already visible behind it. The
  // greeting is unaffected — it fires from `fullscreen` (kickoff), never from `bubble`.
  if (onRoot && onboardingRoute !== 'none' && chatMode !== 'bubble') return true;
  const isNewChat = !activeConversationId;
  const homeIsLanding =
    narrow && onRoot && isNewChat && typeof router.query.prefill !== 'string' && !keepChatExpanded;
  return chatMode !== 'bubble' && !homeIsLanding;
}
